import { css } from '@emotion/react';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope';
import { type FC } from 'react';
import {
  ButtonSize, ButtonStyle,
} from '~/_shared/baseComponents/buttons/button/button.types';
import { TextInputAuthComponent } from '~/_shared/baseComponents/inputs';
import { FormErrorMessageComponent } from '~/_shared/components/formErrorMessage/formErrorMessage.component';
import { CONFIG } from '~/_shared/constants/config';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import { inputWrapperStyle } from '~/authorization/loginPage/loginForm/loginForm.component';
import { authorizationPagesErrorStyle } from '../../authorizationPages.styles';
import { AuthorizationButtonComponent } from '../../submitButton/authorizationSubmitButton.component';
import { SsoButtonsComponent } from '../sso/ssoButtons.component';
import { LoginFormLinksComponent } from './loginFormLinks.component';

const defaultCursor = css({ cursor: 'default' });

const buttonTooltipWrapperStyle = css({
  display: 'block',
  marginBottom: 16,
  width: '100%',
});

const ssoDividerWrapperStyle = css({
  margin: '24px 0',
  display: 'flex',
  alignItems: 'center',
});

const ssoDividerTextStyle = (theme: Theme) => css(defaultCursor, {
  color: theme.textColors.secondary,
  fontSize: 14,
  fontWeight: 400,
  padding: '0 16px',
  textAlign: 'center',
  textTransform: 'uppercase',
  width: '100%',
});

const ssoSectionStyle = css({ marginBottom: 24 });

const legacyLoginSectionStyle = css({
  marginTop: 72,
  textAlign: 'center',
});

const legacyDividerWrapperStyle = css({
  marginBottom: 24,
  display: 'flex',
  alignItems: 'center',
});

const legacyDividerLineStyle = (theme: Theme) => css({
  flex: 1,
  height: 1,
  backgroundColor: theme.lineColors.basePrimary,
});

const legacyDividerTextStyle = (theme: Theme) => css(defaultCursor, {
  alignItems: 'center',
  color: theme.textColors.secondary,
  display: 'flex',
  fontSize: 12,
  gap: 8,
  padding: '0 12px',
  whiteSpace: 'nowrap',
});

const legacyBadgeStyle = (theme: Theme) => css({
  backgroundColor: theme.tagColors.greenBackgroundColor,
  color: theme.tagColors.primaryTextColor,
  borderRadius: 4,
  padding: '2px 8px',
  fontSize: 11,
  fontWeight: 400,
});

export type FormField = Readonly<{
  value: string;
  errors: ReadonlyArray<string>;
  onBlur: () => void;
  onChange: (value: string) => void;
}>;

type LoginFormOptionsViewProps = Readonly<{
  email: FormField;
  isLoading: boolean;
  sso: Readonly<{ onGoogleLogin?: () => void; onMicrosoftLogin?: () => void }>;
  onShowEmailForm: () => void;
  onLegacyLogin?: () => void;
}>;

export const LoginFormOptionsView: FC<LoginFormOptionsViewProps> = (props) => {
  const [t] = useTranslation();

  return (
    <>
      <div css={inputWrapperStyle}>
        <TextInputAuthComponent
          autoComplete="username"
          hasError={props.email.errors.length > 0}
          icon={faEnvelope}
          isDisabled={props.isLoading}
          name="email"
          onBlur={props.email.onBlur}
          onChange={props.email.onChange}
          placeholder={t('Email')}
          type="email"
          value={props.email.value}
        />

        {props.email.errors.length > 0 && (
          <FormErrorMessageComponent
            css={authorizationPagesErrorStyle}
            messages={props.email.errors}
          />
        )}
      </div>

      <div css={buttonTooltipWrapperStyle}>
        <AuthorizationButtonComponent
          buttonStyle={ButtonStyle.Primary}
          data-testid="login-continue-with-email"
          isDisabled={props.isLoading}
          isLoading={false}
          onClick={props.onShowEmailForm}
        >
          {t('login.email.signInWithEmail')}
        </AuthorizationButtonComponent>
      </div>

      <LoginFormLinksComponent isLoading={props.isLoading} />

      {CONFIG.ENABLE_SSO_LOGIN && (
        <>
          <div css={ssoDividerWrapperStyle}>
            <div css={ssoDividerTextStyle}>{t('or')}</div>
          </div>
          <div css={ssoSectionStyle}>
            <SsoButtonsComponent
              isDisabled={props.isLoading}
              onGoogleLogin={props.sso.onGoogleLogin}
              onMicrosoftLogin={props.sso.onMicrosoftLogin}
            />
          </div>
        </>
      )}

      <div css={legacyLoginSectionStyle}>
        <div css={legacyDividerWrapperStyle}>
          <div css={legacyDividerLineStyle} />
          <div css={legacyDividerTextStyle}>
            {t('login.tagline.legacy')} <span css={legacyBadgeStyle}>{t('Legacy')}</span>
          </div>
          <div css={legacyDividerLineStyle} />
        </div>
        <AuthorizationButtonComponent
          buttonStyle={ButtonStyle.Quaternary}
          size={ButtonSize.Medium}
          isLoading={false}
          isDisabled={props.isLoading}
          onClick={props.onLegacyLogin}
        >
          {t('login.tag.legacy')}
        </AuthorizationButtonComponent>
      </div>
    </>
  );
};
